import type { ComponentType } from 'react';
import { IconBack } from '@belong/icons';
import { BlogCarouselSlice } from 'src/features/cms/slices/blog-carousel-slice/blog-carousel-slice';
import { CoverageMapSlice } from 'src/features/cms/slices/coverage-map-slice/coverage-map-slice';
import { GoogleStarsSlice } from 'src/features/cms/slices/google-stars-slice/google-stars-slice';
import { GuaranteedRentWithBelongXCardSlice } from 'src/features/cms/slices/guaranteed-rent-with-belong-x-card-slice/guaranteed-rent-with-belong-x-card-slice';
import { HalfTextHalfImageSlice } from 'src/features/cms/slices/half-text-half-image-slice/half-text-half-image-slice';
import { HomeownersCountSlice } from 'src/features/cms/slices/homeowners-count-slice/homeowners-count-slice';
import { NumberedStepsSectionSlice } from 'src/features/cms/slices/numbered-steps-section-slice/numbered-steps-section-slice';
import { ReferralCardSlice } from 'src/features/cms/slices/referral-card-slice/referral-card-slice';
import { SellHomeSectionSlice } from 'src/features/cms/slices/sell-home-section-slice/sell-home-section-slice';
import { VacancyBannerSlice } from 'src/features/cms/slices/vacancy-banner-slice/vacancy-banner-slice';
import { VideoSectionSlice } from 'src/features/cms/slices/video-section-slice/video-section-slice';
import { ComparisonTableSlice } from '../cms/slices/comparison-table-slice/comparison-table-slice';
import { MediaSlice } from '../cms/slices/media-slice/media-slice';
import { TestimonialsCarouselSlice } from '../cms/slices/testimonial-carousel-slice/testimonial-carousel-slice';
import { Blockquote } from './components/blockquote/blockquote';
import { DataTableSlice } from './components/data-table-slice/data-table-slice';
import { CalculatorSection } from './components/calculator-section/calculator-section';
import { CtaSlice } from './components/cta-slice/cta-slice';
import { HomeownersPriceTableSlice } from './components/homeowners-price-table-slice/homeowners-price-table-slice';
import { ImageSlice } from './components/image-slice/image-slice';
import { MonthYearRentSlice } from './components/month-year-rent-slice/month-year-rent-slice';
import { RichTextSlice } from './components/rich-text-slice/rich-text-slice';
import { SectionSlice } from './components/section-slice/section-slice';
import { SpaceSlice } from './components/space-slice/space-slice';

type Calculator = 'calculator';
type CTA = 'cta';
type PriceFold = 'price_fold';
type PriceTable = 'price_table';
type Section = 'section';
type Space = 'space';
type TestimonialsCarouselType = 'testimonials_carousel';
type Text = 'text';

type Blockquote = 'blockquote';
type BlogCarousel = 'blog_carousel';
type CoverageMap = 'coverage_map';
type GoogleStars = 'google_stars';
type HomeownersCount = 'homeowners_count';
type NumberedStepsSection = 'numbered_steps_section';
type ReferralCard = 'referral_card';
type SellHomeSection = 'sell_home_section';
type HalfTextHalfImage = 'half_text_half_image';
type VacancyBanner = 'vacancy_banner';
type VideoSection = 'video_section';
type ComparisonTable = 'comparisons_table';
type GuaranteedRentWithBelongXCard = 'guaranteed_rent_with_belong_x_card';
type DataTable = 'data_table';
type Image = 'image';
type Media = 'media';

export type BlogAuthor = {
  uid: string;
  data: {
    first_name: string;
    last_name: string;
    about_me: string;
    title: string;
    profile_image_url: {
      url: string;
    };
  };
};

export type SliceType =
  | Calculator
  | CTA
  | PriceFold
  | PriceTable
  | Section
  | Space
  | TestimonialsCarouselType
  | Text
  | Blockquote
  | BlogCarousel
  | CoverageMap
  | GoogleStars
  | HomeownersCount
  | NumberedStepsSection
  | ReferralCard
  | SellHomeSection
  | HalfTextHalfImage
  | VacancyBanner
  | VideoSection
  | ComparisonTable
  | GuaranteedRentWithBelongXCard
  | Image
  | Media
  | DataTable;

// `ComponentType<any>`, not `(props: unknown) => JSX.Element`: a component
// whose props are `unknown` accepts none at all in JSX, so every caller that
// spreads a slice's own fields onto it was rejected. Each slice here takes a
// different prop shape, chosen at runtime by `slice_type`, so there is no one
// type to name.
export const SLICE_COMPONENT_MAP: Record<SliceType, ComponentType<any>> = {
  calculator: CalculatorSection,
  cta: CtaSlice,
  price_fold: MonthYearRentSlice,
  price_table: HomeownersPriceTableSlice,
  section: SectionSlice,
  space: SpaceSlice,
  testimonials_carousel: TestimonialsCarouselSlice,
  text: RichTextSlice,
  blockquote: Blockquote,
  image: ImageSlice,
  media: MediaSlice,

  referral_card: ReferralCardSlice,
  blog_carousel: BlogCarouselSlice,
  coverage_map: CoverageMapSlice,
  google_stars: GoogleStarsSlice,
  homeowners_count: HomeownersCountSlice,
  vacancy_banner: VacancyBannerSlice,
  video_section: VideoSectionSlice,
  sell_home_section: SellHomeSectionSlice,
  half_text_half_image: HalfTextHalfImageSlice,
  numbered_steps_section: NumberedStepsSectionSlice,
  comparisons_table: ComparisonTableSlice,
  guaranteed_rent_with_belong_x_card: GuaranteedRentWithBelongXCardSlice,
  data_table: DataTableSlice,
};

export const nextAndPrevButtonsContent = [
  [
    <div className="mr-2xs" key="next-text">
      Next
    </div>,
    <IconBack width={16} height={15} className="rotate-180" key="next-icon" />,
  ],
  [
    <IconBack width={16} height={15} className="mr-2xs" key="back-icon" />,
    'Previous',
  ],
];
